// Torneos — capa de API. Molde de `collectionsApi.js` (el patrón bueno: wrapper con
// manejo de errores, en vez de fetch inline en cada componente).
//
// ⚠️ El manejo de 1002 está DUPLICADO de collectionsApi.js a propósito y a disgusto: es la
// misma lógica sutil en dos lugares. Acá hace falta igual porque los torneos se scopean por
// sucursal, que es exactamente el caso que la motiva (ver abajo). Vale unificarlas en un
// `apiClient.js` compartido, pero eso toca un archivo que hoy anda y no es parte de esto.

import { endSession } from './session';

const config = process.env;

const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;

const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

// ── 1002: "sesión vencida" NO es lo mismo que "tu rol no alcanza acá" ──
// El backend responde 1002 en los dos casos con el mismo body. Y el rol efectivo se recalcula
// POR SUCURSAL, así que un encargado que en la sucursal seleccionada figura como cajero recibe
// 1002 con la sesión perfecta. Tratarlo como sesión vencida lo desloguea de golpe y le hace
// perder lo que tuviera a medio cargar — en medio de un torneo, eso es carísimo.
// `session/checkSessionAdmin` es tokenless y contesta si el token vive: sólo si dice que no,
// se sale al login.
const SESSION_EXPIRED = 'La sesión expiró.';
const FORBIDDEN = 'Tu usuario no tiene permiso para esto en la sucursal seleccionada.';
const UNKNOWN_SESSION = 'No pudimos verificar tu sesión. Recargá la página y, si sigue, volvé a entrar.';

let probeInFlight = null;

async function probeSessionAlive() {
    const token = localStorage.getItem('token');
    if (!token) return false;
    if (probeInFlight == null) {
        probeInFlight = (async () => {
            try {
                // El token va en el HEADER 'access-token': el endpoint lo lee de ahí
                // (routes/session.js) y NO del body. Mandarlo en el body hacía que el probe
                // contestara SIEMPRE isValidToken:false, con lo cual la mitigación quedaba
                // INVERTIDA: en vez de preservar la sesión ante un 1002 de permisos, la
                // destruía siempre. Peor que no tenerla.
                const res = await fetch(`${apiBase()}session/checkSessionAdmin`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({}),
                });
                const body = await res.json();
                if (body && body.data && typeof body.data.isValidToken === 'boolean') return body.data.isValidToken;
                return null;
            } catch (e) {
                return null;   // no se pudo averiguar: NO se destruye nada
            } finally {
                setTimeout(() => { probeInFlight = null; }, 0);
            }
        })();
    }
    return probeInFlight;
}

async function request(path, options = {}) {
    let res;
    try {
        res = await fetch(apiBase() + path, {
            ...options,
            headers: { ...authHeaders(), ...(options.headers || {}) },
        });
    } catch (networkError) {
        const err = new Error('No pudimos hablar con el servidor. Revisá la conexión y reintentá.');
        err.code = 0;
        throw err;
    }

    let body = null;
    try { body = await res.json(); } catch (e) { body = null; }
    const code = body && Number(body.error) > 0 ? Number(body.error) : 0;

    if (code === 1002) {
        const alive = await probeSessionAlive();
        const err = new Error(alive === true ? FORBIDDEN : alive === false ? SESSION_EXPIRED : UNKNOWN_SESSION);
        err.code = 1002;
        if (alive === false) endSession();
        throw err;
    }

    if (!res.ok || code) {
        // El motivo de negocio viaja anidado en data.message (ENI-116/ENI-210); el message de
        // primer nivel es genérico por código y para el 4000 dice "La compra no existe", que
        // en una pantalla de torneos no significa nada.
        const msg = (body && body.data && body.data.message) || (body && body.message)
            || 'Ocurrió un error procesando la solicitud.';
        const err = new Error(msg);
        err.code = code;
        err.body = body;
        throw err;
    }

    return body ? body.data : null;
}

const post = (path, payload) => request(path, { method: 'POST', body: JSON.stringify(payload || {}) });
const patch = (path, payload) => request(path, { method: 'PATCH', body: JSON.stringify(payload || {}) });
const put = (path, payload) => request(path, { method: 'PUT', body: JSON.stringify(payload || {}) });

// ── torneos ──
// standalone=false: la lista trae TAMBIÉN los torneos de un evento. Con el default (sólo
// sueltos), un torneo creado desde un evento no aparecía en ningún listado y el único camino de
// vuelta era el evento — se leía como que no había quedado creado.
export const listTournaments = () => request('manage-tournaments?standalone=false');
export const createTournament = (payload) => post('manage-tournaments', payload);
export const getTournament = (id) => request(`manage-tournaments/${id}`);
export const updateTournament = (id, payload) => patch(`manage-tournaments/${id}`, payload);

// ── atajo desde el evento (el recurso sigue viviendo en /manage-tournaments) ──
export const getTournamentByEvent = (eventId) => request(`manage-events/${eventId}/tournament`);
export const createTournamentForEvent = (eventId, payload) => post(`manage-events/${eventId}/tournament`, payload || {});

// ── participantes ──
export const addParticipantsBulk = (id, names) => post(`manage-tournaments/${id}/participants/bulk`, { names });
export const syncParticipants = (id) => post(`manage-tournaments/${id}/participants/sync`, {});
export const updateParticipant = (id, pid, payload) => patch(`manage-tournaments/${id}/participants/${pid}`, payload);
// Pasar lista (torneo de un día): la lista reemplaza a la anterior, para la PRÓXIMA ronda.
export const rollCall = (id, presentIds) => put(`manage-tournaments/${id}/roll-call`, { presentIds });
export const getContacts = (id) => request(`manage-tournaments/${id}/contacts`);

// ── enganchar un torneo suelto a un evento (y desengancharlo) ──
export const getLinkableEvents = (id) => request(`manage-tournaments/${id}/linkable-events`);
export const linkEvent = (id, eventId) => put(`manage-tournaments/${id}/event`, { eventId });
export const unlinkEvent = (id) => request(`manage-tournaments/${id}/event`, { method: 'DELETE' });

// ── desempate del juez y bitácora ──
export const setTiebreak = (id, participantIds, clear = false) => put(`manage-tournaments/${id}/tiebreaks`, { participantIds, clear });
export const getLog = (id) => request(`manage-tournaments/${id}/log`);

// ── planilla para Devir ──
export const exportCheck = (id) => request(`manage-tournaments/${id}/export/check`);

// La planilla es un ARCHIVO: no pasa por `request`, que espera JSON. Pero el backend contesta los
// errores como HTTP 200 con JSON, así que se mira el content-type antes de tratarlo como archivo
// (mismo cuidado que utils/invoiceFile.js). Se baja con el nombre que manda el servidor.
export async function downloadExport(id, format) {
    const res = await fetch(`${apiBase()}manage-tournaments/${id}/export?format=${format}`, {
        headers: { 'access-token': localStorage.getItem('token') },
    });
    if ((res.headers.get('content-type') || '').includes('json')) {
        const body = await res.json().catch(() => null);
        if (body && Number(body.error) === 1002) {
            const alive = await probeSessionAlive();
            if (alive === false) endSession();
            throw new Error(alive === true ? FORBIDDEN : SESSION_EXPIRED);
        }
        throw new Error((body && body.data && body.data.message) || (body && body.message) || 'No se pudo generar la planilla.');
    }
    const disp = res.headers.get('content-disposition') || '';
    const m = disp.match(/filename="([^"]+)"/);
    const name = m ? m[1] : `planilla.${format}`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ── jornadas (ligas) ──
export const getSessions = (id) => request(`manage-tournaments/${id}/sessions`);
export const createSession = (id, playedOn) => post(`manage-tournaments/${id}/sessions`, { playedOn });
export const updateSession = (id, sid, payload) => patch(`manage-tournaments/${id}/sessions/${sid}`, payload);
export const setAttendance = (id, sid, participantIds) => put(`manage-tournaments/${id}/sessions/${sid}/attendance`, { participantIds });
export const closeSession = (id, sid) => patch(`manage-tournaments/${id}/sessions/${sid}/close`, {});
export const reopenSession = (id, sid) => patch(`manage-tournaments/${id}/sessions/${sid}/reopen`, {});
export const rebuildSession = (id, sid) => patch(`manage-tournaments/${id}/sessions/${sid}/rebuild`, {});

// ── rondas y mesas ──
export const getPreview = (id) => request(`manage-tournaments/${id}/preview`);
export const getStandings = (id) => request(`manage-tournaments/${id}/standings`);
export const generateRound = (id) => post(`manage-tournaments/${id}/rounds`, {});
export const generateSemifinal = (id) => post(`manage-tournaments/${id}/semifinal`, {});
export const generateFinal = (id) => post(`manage-tournaments/${id}/final`, {});
// Desarma la ÚLTIMA etapa armada (la final, o la semifinal si todavía no hay final).
export const discardFinal = (id) => request(`manage-tournaments/${id}/final`, { method: 'DELETE' });
export const manualRound = (id, tables) => post(`manage-tournaments/${id}/rounds/manual`, { tables });
export const getRound = (id, rid) => request(`manage-tournaments/${id}/rounds/${rid}`);
export const rebuildRound = (id, rid) => patch(`manage-tournaments/${id}/rounds/${rid}/rebuild`, {});
export const reseatRound = (id, rid, tables) => put(`manage-tournaments/${id}/rounds/${rid}/seating`, { tables });
export const updateRound = (id, rid, payload) => patch(`manage-tournaments/${id}/rounds/${rid}`, payload);
export const reportTable = (id, tableId, results) => put(`manage-tournaments/${id}/tables/${tableId}/results`, { results });
export const clearTable = (id, tableId) => request(`manage-tournaments/${id}/tables/${tableId}/results`, { method: 'DELETE' });
// Corregir una mesa de una ronda ya cerrada: sin `confirm` devuelve el impacto sin escribir nada.
export const correctTable = (id, tableId, results, confirm = false) => post(`manage-tournaments/${id}/tables/${tableId}/correct`, { results, confirm });
export const setSeatOutcome = (id, tableId, seatId, outcome) => patch(`manage-tournaments/${id}/tables/${tableId}/seats/${seatId}`, { outcome });

// ── vocabulario de UI ──
export const ESTADO_TORNEO = {
    setup: { label: 'En armado', badge: 'badge-secondary' },
    running: { label: 'En juego', badge: 'badge-warning' },
    final: { label: 'Mesa final', badge: 'badge-primary' },
    finished: { label: 'Terminado', badge: 'badge-success' },
};

export const MOTIVO_BAJA = {
    withdrew: 'Abandonó',
    no_show: 'No se presentó',
    disqualified: 'Descalificado',
};

export const ESTADO_RONDA = {
    draft: { label: 'Sin abrir', badge: 'badge-secondary' },
    open: { label: 'En juego', badge: 'badge-warning' },
    closed: { label: 'Cerrada', badge: 'badge-success' },
};

// "10/10 17:00" — la fecha del evento tal como la manda el backend (hora de pared, sin zona).
export const fechaEvento = (s) => {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    return m ? `${m[3]}/${m[2]} ${m[4]}:${m[5]}` : '';
};
